import React, { Fragment, useState, useEffect } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton';
import { Backdrop } from 'Components/frameworks/backdrop/Backdrop';
import Image from 'Components/frameworks/image/Image';
import type { RatioDisplay } from 'Components/frameworks/image/Image';
import { SliderHandler } from 'Components/frameworks/slider-handler/SliderHandler';
export type ImageSlideShowProps = {
  centerPadding?: number;
  className?: string;
  closeLabel?: string;
  fadeUnseenItems?: boolean;
  galleryLabel?: string;
  groupKey?: string;
  hasFullScreenMode?: boolean;
  hasOverlappingControls?: boolean;
  hasOverlappingClick?: boolean;
  hasInfiniteScroll?: boolean;
  hasLazyLoading?: boolean;
  itemClassName?: string;
  media: Array<{
    alt?: string;
    onClick?: () => void;
    src: string;
  }>;
  nbItemsToShow?: number;
  nextLabel?: string;
  onChangeIndex?: (arg0: number) => void;
  previousLabel?: string;
  ratio?: string;
  ratioDisplay?: RatioDisplay;
  spacerWidth?: number;
};
export const ImageSlideShow = ({
  centerPadding,
  className,
  closeLabel,
  fadeUnseenItems,
  galleryLabel,
  groupKey = 'thumbnails',
  hasFullScreenMode,
  hasOverlappingControls,
  hasOverlappingClick,
  hasInfiniteScroll,
  hasLazyLoading,
  itemClassName,
  media = [],
  nbItemsToShow = 1,
  nextLabel,
  onChangeIndex,
  previousLabel,
  ratio = ratioUtils.XL,
  ratioDisplay = 1,
  spacerWidth = 0,
}: ImageSlideShowProps) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    if (onChangeIndex) {
      onChangeIndex(activeIndex);
    }
  }, [activeIndex]);

  if (!media.length) {
    return null;
  }

  const updateActiveIndex = (index) => {
    setActiveIndex(index);
  };

  const _gk = hasFullScreenMode ? null : groupKey;
  const items = media
    .filter(({ src }) => Boolean(src))
    .map(({ alt = '', onClick, src }) => {
      const children = (
        <Image
          altMsg={alt}
          className={itemClassName}
          hasLazyLoading={hasLazyLoading}
          imageUrl={src}
          key={src}
          ratioKey={ratio}
          ratioDisplay={ratioDisplay}
        />
      );
      const isButton = hasFullScreenMode || onClick;
      return (
        <Fragment key={`Image-${src}`}>
          {isButton ? (
            <button
              className="block h-full w-full"
              onClick={() => {
                onClick && onClick();

                if (hasFullScreenMode) {
                  setIsFullScreen(true);
                }
              }}
              type="button"
            >
              {children}
            </button>
          ) : (
            <div className="h-full">{children}</div>
          )}
        </Fragment>
      );
    });
  return (
    <SliderHandler
      centerPadding={centerPadding}
      fadeUnseenItems={fadeUnseenItems}
      hasInfiniteScroll={hasInfiniteScroll}
      hasKeyboardSupport
      hasLazyload
      hasSwipeSupport
      items={items}
      nbItemsToShow={nbItemsToShow}
      onChangeIndex={updateActiveIndex}
      spacerWidth={spacerWidth}
    >
      {({
        hasControls,
        isNextControlDisabled,
        isPreviousControlDisabled,
        itemsNode,
        onNext,
        onPrevious,
      }) => (
        <div className={classnames('group relative', className)}>
          {itemsNode}
          {hasControls && (
            <div
              className={classnames(
                'pointer-events-none absolute inset-0 z-2 m-auto flex w-full items-center justify-between transition-opacity duration-250 ease-in will-change-transform',
                {
                  'px-10 sm:px-20': !hasOverlappingControls,
                },
              )}
              data-testid="ImageSlideShow-controls-parent"
            >
              {!isPreviousControlDisabled && (
                <ArrowButton
                  className={classnames('pointer-events-auto z-2 me-auto shadow-sm', {
                    '-translate-x-1/2 transform': hasOverlappingControls,
                  })}
                  color={COLORS.WHITE}
                  label={previousLabel}
                  onClick={(event) => {
                    onPrevious(event);

                    if (hasOverlappingClick) {
                      event?.stopPropagation();
                    }
                  }}
                />
              )}
              {!isNextControlDisabled && (
                <ArrowButton
                  className={classnames('pointer-events-auto z-2 ms-auto shadow-sm', {
                    'translate-x-1/2 transform': hasOverlappingControls,
                  })}
                  color={COLORS.WHITE}
                  label={nextLabel}
                  onClick={(event) => {
                    onNext(event);

                    if (hasOverlappingClick) {
                      event?.stopPropagation();
                    }
                  }}
                  rotation={180}
                />
              )}
            </div>
          )}
          {hasFullScreenMode && isFullScreen && (
            <Backdrop
              ariaLabel={galleryLabel}
              closeLabel={closeLabel}
              hasCloseButton
              onClose={() => setIsFullScreen(false)}
            >
              <div className="relative w-full">
                {itemsNode}
                {hasControls && (
                  <div className="pointer-events-none absolute inset-0 m-auto flex w-full items-center justify-between px-10 sm:px-20">
                    {!isPreviousControlDisabled && (
                      <ArrowButton
                        className="pointer-events-auto z-2 me-auto shadow-sm"
                        color={COLORS.WHITE}
                        label={previousLabel}
                        onClick={(event) => {
                          onPrevious(event);

                          if (hasOverlappingClick) {
                            event?.stopPropagation();
                          }
                        }}
                      />
                    )}
                    {!isNextControlDisabled && (
                      <ArrowButton
                        className="pointer-events-auto z-2 ms-auto shadow-sm"
                        color={COLORS.WHITE}
                        label={nextLabel}
                        onClick={(event) => {
                          onNext(event);

                          if (hasOverlappingClick) {
                            event?.stopPropagation();
                          }
                        }}
                        rotation={180}
                      />
                    )}
                  </div>
                )}
              </div>
            </Backdrop>
          )}
        </div>
      )}
    </SliderHandler>
  );
};
